import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiUser, FiPackage, FiSearch, FiCalendar, FiFileText, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Toast } from '../../components/UI/Toast';
import { ordenesService } from '../../services/ordenesService';
import { clientesService } from '../../services';
import { useClientes } from '../../hooks/useClientes';
import { useProductos } from '../../hooks/useProductos';
import { useAuth } from '../../contexts/AuthContext';
import './PreOrderForm.scss';

const PreOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  // Obtener la fuente desde el state de navegación
  const sourceFromState = location.state?.source || 'BETEL_NATURAL';
  // Modo cotización: misma UI de pre-orden pero crea un ESTIMATE con precios/totales
  const isCotizacion = location.state?.isCotizacion === true;

  const { clientes, loadClientes } = useClientes();
  const { productos: allProductos, searchProductos, isLoading: productosLoading } = useProductos();

  const [orden, setOrden] = useState({
    client_id: '',
    sale_date: new Date().toISOString().split('T')[0],
    notes: '',
    general_notes: '',
    items: [],
    status: isCotizacion ? 'DRAFT' : 'ON_HOLD', // Cotización: Borrador / Pre-orden: Detenida
    order_type: isCotizacion ? 'ESTIMATE' : 'PRE_ORDER',
    source: isCotizacion ? 'ADMIN' : sourceFromState,
    order_status: 'DETENIDA', // LIBERADA | DETENIDA (por defecto DETENIDA)
    taken_by: user?.full_name || '',
    reviewed_by: '',
    user_id: user?.id || ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');

  // Estados para búsqueda de productos
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  // '' = campo vacío (se normaliza con parseFloat||0 al agregar el ítem);
  // antes iniciaban en 0 y el cero no se podía borrar.
  const [itemPrice, setItemPrice] = useState('');
  const [itemDiscount, setItemDiscount] = useState('');

  // Estados para búsqueda de cliente (click → lista A→Z; escribir ≥3 letras → backend)
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const clientDropdownRef = useRef(null);
  const clientSearchTimer = useRef(null);

  // Es cotización si el tipo es ESTIMATE (vía modal o al editar una cotización existente)
  const esCotizacion = orden.order_type === 'ESTIMATE';

  // Lista de clientes por defecto (al hacer click sin escribir): ordenada alfabéticamente
  const defaultClientOptions = useMemo(
    () =>
      [...(clientes || [])]
        .map((c) => ({ id: c.id, nombre: c.nombreNegocio || c.nombreContacto || `Cliente #${c.id}` }))
        .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [clientes]
  );
  // ≥3 letras → resultados del backend; 1-2 letras → filtro local; vacío → solo 5 (al hacer click)
  const clientSearchTerm = clientSearch.trim();
  const clientOptions =
    clientSearchTerm.length >= 3
      ? clientResults
      : clientSearchTerm.length > 0
        ? defaultClientOptions.filter((c) => c.nombre.toLowerCase().includes(clientSearchTerm.toLowerCase()))
        : defaultClientOptions.slice(0, 5);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);

  // Etiquetas legibles de departamento (product_type) y categoría del producto
  const DEPARTAMENTO_LABELS = {
    BETEL: 'Betel Natural', NATURALANDLAB: 'NaturalAndLab', OTHER_BRANDS: 'Otras Marcas',
    OTRAS_MARCAS: 'Otras Marcas', INGREDIENT: 'Ingredientes', PACKAGING_MATERIAL: 'Material de Empaque'
  };
  const CATEGORIA_LABELS = {
    CAPSULES: 'Cápsulas', POWDERS: 'Polvos', OILS: 'Aceites', TEAS: 'Infusiones',
    CREAMS: 'Cremas', LEAVES: 'Hojas', LIQUIDS: 'Líquidos', SUPPLEMENTS: 'Suplementos',
    TINCTURES: 'Tinturas', TABLETS: 'Tabletas', EXTRACTS: 'Extractos', HERBS: 'Hierbas',
    VITAMINS: 'Vitaminas', MINERALS: 'Minerales', SUPPLIES: 'Insumos', PACKAGING: 'Empaque', OTHER: 'Otros'
  };
  const labelDepartamento = (v) => DEPARTAMENTO_LABELS[String(v || '').toUpperCase()] || v || '';
  const labelCategoria = (v) => CATEGORIA_LABELS[String(v || '').toUpperCase()] || v || '';

  // Totales (sin impuestos) calculados a partir de los ítems
  const getTotals = () =>
    orden.items.reduce((acc, it) => {
      const base = (parseFloat(it.unit_price) || 0) * (parseFloat(it.quantity) || 0);
      const disc = base * ((parseFloat(it.discount_percentage) || 0) / 100);
      acc.subtotal += base;
      acc.descuento += disc;
      acc.total += base - disc;
      return acc;
    }, { subtotal: 0, descuento: 0, total: 0 });

  useEffect(() => {
    loadClientes();
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      loadOrden();
    }
  }, [id, isEditing]);

  // Efecto para búsqueda de productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      searchProductos(productSearch);
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch]);

  // Actualizar resultados cuando cambien los productos
  useEffect(() => {
    if (productSearch.trim().length > 0 && showDropdown) {
      setSearchResults(allProductos);
    }
  }, [allProductos, productSearch, showDropdown]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cerrar el dropdown de clientes al hacer clic fuera
  useEffect(() => {
    const handleClickOutsideClient = (event) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideClient);
    return () => document.removeEventListener('mousedown', handleClickOutsideClient);
  }, []);

  const loadOrden = async () => {
    setIsLoading(true);
    const result = await ordenesService.getOrdenById(id);
    if (result.success) {
      const ordenData = {
        ...result.data,
        order_type: result.data.order_type || 'PRE_ORDER',
        source: result.data.source || sourceFromState,
        client_id: result.data.client_id ?? result.data.client?.id ?? '',
        user_id: result.data.user_id ?? result.data.user?.id ?? '',
        // El backend persiste el estatus en `status`; derivamos el toggle desde ahí
        order_status: result.data.status === 'RELEASED' ? 'LIBERADA' : 'DETENIDA',
        taken_by: result.data.taken_by || '',
        reviewed_by: result.data.reviewed_by || ''
      };
      setOrden(ordenData);
      const clientName = result.data.client?.company_name || result.data.client?.full_name || '';
      if (clientName) setClientSearch(clientName);
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleInputChange = (field, value) => {
    setOrden(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error de validación si existe
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // ── Búsqueda de cliente (combo) ──────────────────────────────────────────
  const searchClientesBackend = async (term) => {
    setClientsLoading(true);
    try {
      const res = await clientesService.getClientes({ search: term, limit: 50, is_active: true });
      if (res.success) {
        const mapped = (res.data || []).map((c) => ({
          id: c.id,
          nombre: c.company_name || c.full_name || `Cliente #${c.id}`
        }));
        setClientResults(mapped.sort((a, b) => a.nombre.localeCompare(b.nombre)));
      }
    } catch (err) {
      console.error('Error buscando clientes:', err);
    } finally {
      setClientsLoading(false);
    }
  };

  const handleClientInputChange = (value) => {
    setClientSearch(value);
    setShowClientDropdown(true);
    // Si ya había un cliente elegido y el usuario edita el texto, se invalida hasta re-elegir
    if (orden.client_id) handleInputChange('client_id', '');
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    const term = value.trim();
    if (term.length >= 3) {
      clientSearchTimer.current = setTimeout(() => searchClientesBackend(term), 350);
    } else {
      setClientResults([]);
    }
  };

  const handleClientSelect = (cliente) => {
    handleInputChange('client_id', cliente.id);
    setClientSearch(cliente.nombre);
    setShowClientDropdown(false);
    setClientResults([]);
  };

  const handleProductSelect = (product) => {
    // Cerrar dropdown inmediatamente
    setShowDropdown(false);
    setSearchResults([]);

    // Establecer producto seleccionado y prefijar su precio (para cotización)
    setSelectedProduct(product);
    setItemPrice(product.precio || '');

    // Limpiar campo de búsqueda
    setProductSearch('');
  };

  const addItemToOrden = () => {
    const qty = parseFloat(itemQuantity);
    if (!selectedProduct || !qty || qty <= 0) {
      setError('Seleccione un producto y especifique una cantidad válida');
      return;
    }

    const unitPrice = esCotizacion ? (parseFloat(itemPrice) || 0) : (selectedProduct.precio || 0);
    const discountPct = esCotizacion ? (parseFloat(itemDiscount) || 0) : 0;
    const base = unitPrice * qty;
    const discountAmount = base * (discountPct / 100);

    const newItem = {
      product_id: selectedProduct.id,
      product_name: selectedProduct.nombreProducto,
      product_code: selectedProduct.codigo,
      quantity: qty,
      quantity_requested: qty,
      unit_price: unitPrice,
      discount_percentage: discountPct,
      discount_amount: discountAmount,
      subtotal: base - discountAmount,
      total: base - discountAmount,
      validation_status: 'PENDING',
      stock_available: selectedProduct.stock || 0
    };

    setOrden(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
    setItemPrice('');
    setItemDiscount('');
    setError(null);
  };

  const removeItem = (index) => {
    setOrden(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!orden.client_id) {
      errors.client_id = 'Cliente es requerido';
    }
    if (!orden.sale_date) {
      errors.sale_date = 'Fecha es requerida';
    }
    if (orden.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }

    setValidationErrors(errors);

    // Si hay errores, construir mensaje y mostrar toast
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).filter(msg => msg);
      const toastMsg = `Por favor corrige los siguientes errores: ${errorMessages.join(', ')}`;
      setToastMessage(toastMsg);
      setToastType('error');
      setShowToast(true);
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const totals = getTotals();
    const ordenData = {
      ...orden,
      client_id: parseInt(orden.client_id),
      user_id: parseInt(orden.user_id),
      order_type: esCotizacion ? 'ESTIMATE' : 'PRE_ORDER',
      sale_type: 'PRODUCT',
      priority: 'MEDIUM',
      // Cotización: Borrador. Pre-orden: estatus según el selector LIBERADA/DETENIDA.
      status: esCotizacion
        ? 'DRAFT'
        : (orden.order_status === 'LIBERADA' ? 'RELEASED' : 'ON_HOLD'),
      subtotal: totals.subtotal,
      tax_amount: 0,
      discount_amount: totals.descuento,
      total: totals.total
    };

    const result = isEditing
      ? await ordenesService.updateOrden(id, ordenData)
      : await ordenesService.createOrden(ordenData);

    if (result.success) {
      navigate('/system/ventas');
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate('/system/ventas');
  };

  const getSourceLabel = (source) => {
    const labels = {
      'BETEL_NATURAL': 'Betel Natural',
      'NATURALAND_LAB': 'Naturaland Lab',
      'BETEL_NATURAL_VIERNES': 'Betel Natural Viernes'
    };
    return labels[source] || source;
  };

  const getSourceIcon = (source) => {
    const icons = {
      'BETEL_NATURAL': '🌿',
      'NATURALAND_LAB': '🧪',
      'BETEL_NATURAL_VIERNES': '📅'
    };
    return icons[source] || '📦';
  };

  if (isLoading) {
    const loadingMsg = isEditing
      ? (esCotizacion ? 'Cargando cotización...' : 'Cargando pre-orden...')
      : 'Preparando formulario...';
    return <LoadingGrid message={loadingMsg} />;
  }

  const totals = getTotals();

  return (
    <div className="preorder-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing
            ? (esCotizacion ? 'Editar Cotización' : 'Editar Pre-Orden')
            : (esCotizacion ? 'Nueva Cotización' : 'Nueva Pre-Orden')}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            <FiSave size={16} />
            {isEditing ? 'Actualizar' : (esCotizacion ? 'Crear Cotización' : 'Crear Pre-Orden')}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="preorder-form">
        {/* Card 1: Información de la Pre-Orden */}
        <div className="preorder-header-card">
          <div className="preorder-source-badge">
            <span className="source-icon">{esCotizacion ? '📄' : getSourceIcon(orden.source)}</span>
            <span className="source-label">{esCotizacion ? 'COTIZACIÓN' : getSourceLabel(orden.source)}</span>
          </div>

          {/* Notas Generales */}
          <div className="general-notes-box">
            <div className="general-notes-header">NOTAS GENERALES:</div>
            <div className="general-notes-content">
              <div className="general-notes-section">
                <strong>TIEMPO META ENTREGAS</strong>
                <ul>
                  <li>"Órdenes chicas y de online" que llegan antes de las 12 pm → Salir mismo día</li>
                  <li>"Órdenes medianas" → Salir entre 2-3 días</li>
                  <li>"Órdenes grandes" → Salir de 3 a 5 días</li>
                </ul>
              </div>
              <div className="general-notes-checks">
                <div className="check-item">☑ Check for B-day Info</div>
                <div className="check-item">☑ Check for CA Seller's permit paper</div>
              </div>
            </div>
          </div>

          <div className="preorder-header-grid">
            {/* Cliente */}
            <div className="preorder-field">
              <label className="preorder-label">Cliente *</label>
              <div ref={clientDropdownRef} style={{ position: 'relative' }}>
                <input
                  type="text"
                  className={`preorder-input ${validationErrors.client_id ? 'preorder-input--error' : ''}`}
                  placeholder="Haz clic o escribe (mín. 3 letras) para buscar..."
                  value={clientSearch}
                  onChange={(e) => handleClientInputChange(e.target.value)}
                  onFocus={(e) => {
                    setShowClientDropdown(true);
                    e.target.select();
                    if (clientSearchTerm.length >= 3 && clientResults.length === 0 && !clientsLoading) {
                      searchClientesBackend(clientSearchTerm);
                    }
                  }}
                  autoComplete="off"
                />
                {clientsLoading && (
                  <div className="search-combo-loading"><div className="spinner-sm"></div></div>
                )}
                {showClientDropdown && (
                  <div className="product-dropdown">
                    {clientsLoading ? (
                      <div className="product-dropdown-item" style={{ cursor: 'default', color: '#888' }}>
                        Buscando...
                      </div>
                    ) : clientOptions.length > 0 ? (
                      clientOptions.map((cliente) => (
                        <div
                          key={cliente.id}
                          className="product-dropdown-item"
                          onClick={(e) => { e.stopPropagation(); handleClientSelect(cliente); }}
                        >
                          <div className="product-dropdown-icon">
                            <FiUser size={20} />
                          </div>
                          <div className="product-dropdown-info">
                            <div className="product-dropdown-name">{cliente.nombre}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="product-dropdown-item" style={{ cursor: 'default', color: '#888' }}>
                        {clientSearchTerm.length >= 3
                          ? 'Sin resultados'
                          : clientSearchTerm.length > 0
                            ? 'Escribe al menos 3 letras para buscar en el sistema...'
                            : 'No hay clientes cargados'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {validationErrors.client_id && (
                <span className="preorder-error">{validationErrors.client_id}</span>
              )}
            </div>

            {/* Fecha */}
            <div className="preorder-field">
              <label className="preorder-label">Fecha de Orden *</label>
              <input
                type="date"
                className={`preorder-input ${validationErrors.sale_date ? 'preorder-input--error' : ''}`}
                value={orden.sale_date}
                onChange={(e) => handleInputChange('sale_date', e.target.value)}
                required
              />
              {validationErrors.sale_date && (
                <span className="preorder-error">{validationErrors.sale_date}</span>
              )}
            </div>

            {/* Estatus de Orden (solo pre-órdenes; las cotizaciones no usan LIBERADA/DETENIDA) */}
            {!esCotizacion && (
              <div className="preorder-field">
                <label className="preorder-label">Estatus de Orden *</label>
                <div className="status-buttons">
                  <button
                    type="button"
                    className={`status-btn ${orden.order_status === 'LIBERADA' ? 'status-btn--active status-btn--released' : ''}`}
                    onClick={() => handleInputChange('order_status', 'LIBERADA')}
                  >
                    <FiCheckCircle size={18} />
                    LIBERADA
                  </button>
                  <button
                    type="button"
                    className={`status-btn ${orden.order_status === 'DETENIDA' ? 'status-btn--active status-btn--stopped' : ''}`}
                    onClick={() => handleInputChange('order_status', 'DETENIDA')}
                  >
                    <FiAlertCircle size={18} />
                    DETENIDA
                  </button>
                </div>
              </div>
            )}

            {/* Nombre de quien tomó la orden */}
            <div className="preorder-field">
              <label className="preorder-label">Nombre de quien tomó la orden</label>
              <input
                type="text"
                className="preorder-input"
                value={orden.taken_by}
                onChange={(e) => handleInputChange('taken_by', e.target.value)}
                placeholder="Nombre completo (opcional)"
              />
            </div>

            {/* Nombre de quien revisó la orden (solo visible al convertir a ORDER) */}
            {orden.order_type === 'ORDER' && (
              <div className="preorder-field">
                <label className="preorder-label">Nombre de quien revisó la orden</label>
                <input
                  type="text"
                  className="preorder-input"
                  value={orden.reviewed_by}
                  onChange={(e) => handleInputChange('reviewed_by', e.target.value)}
                  placeholder="Nombre completo (opcional)"
                />
              </div>
            )}

            {/* Notas - Ocupa 2 columnas */}
            <div className="preorder-field preorder-field--full">
              <label className="preorder-label">Notas de la Orden</label>
              <textarea
                className="preorder-input preorder-textarea"
                placeholder="Notas adicionales específicas de esta orden..."
                value={orden.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Card 2: Buscador de Productos */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la {esCotizacion ? 'cotización' : 'pre-orden'}</p>
            </div>
          </div>

          <div className="product-search-body">
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder="Buscar por nombre de producto o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
              />
              {productosLoading && (
                <div className="search-combo-loading">
                  <div className="spinner-sm"></div>
                </div>
              )}

              {/* Dropdown de resultados */}
              {showDropdown && searchResults.length > 0 && (
                <div className="product-dropdown">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      className="product-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductSelect(product);
                      }}
                    >
                      <div className="product-dropdown-icon">
                        <FiPackage size={20} />
                      </div>
                      <div className="product-dropdown-info">
                        <div className="product-dropdown-name">{product.nombreProducto}</div>
                        <div className="product-dropdown-meta">
                          {product.departamento && (
                            <span className="meta-code">{labelDepartamento(product.departamento)}</span>
                          )}
                          {product.departamento && product.categoria && (
                            <span className="meta-separator">·</span>
                          )}
                          {product.categoria && <span>{labelCategoria(product.categoria)}</span>}
                          {(product.departamento || product.categoria) && (
                            <span className="meta-separator">·</span>
                          )}
                          <span className="meta-stock">Stock: {product.stock}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">{selectedProduct.nombreProducto}</h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Stock: {selectedProduct.stock}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(e.target.value)}
                      min="1"
                      step="1"
                      className="control-input"
                    />
                  </div>
                  {esCotizacion && (
                    <>
                      <div className="control-group">
                        <label className="control-label">Precio Unit.</label>
                        <input
                          type="number"
                          value={itemPrice}
                          onChange={(e) => setItemPrice(e.target.value)}
                          min="0"
                          step="1"
                          className="control-input"
                        />
                      </div>
                      <div className="control-group">
                        <label className="control-label">Desc. %</label>
                        <input
                          type="number"
                          value={itemDiscount}
                          onChange={(e) => setItemDiscount(e.target.value)}
                          min="0"
                          max="100"
                          step="1"
                          className="control-input"
                        />
                      </div>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToOrden}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Lista de Productos */}
        {orden.items.length > 0 ? (
          <div className="preorder-products-card">
            <div className="products-card-header">
              <div className="products-header-icon">
                <FiPackage size={24} />
              </div>
              <div className="products-header-content">
                <h3 className="products-header-title">Productos en la {esCotizacion ? 'Cotización' : 'Pre-Orden'}</h3>
                <p className="products-header-subtitle">{orden.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="products-list">
              {orden.items.map((item, index) => (
                <div key={index} className="product-item">
                  <div className="product-item-number">{index + 1}.</div>
                  <div className="product-item-info">
                    <div className="product-item-name">{item.product_name}</div>
                  </div>
                  <div className="product-item-quantity">
                    <span className="quantity-label">Cantidad:</span>
                    <span className="quantity-value">{item.quantity}</span>
                  </div>
                  {esCotizacion && (
                    <div className="product-item-pricing">
                      <span className="price-value">{formatCurrency(item.unit_price)}</span>
                      {item.discount_percentage > 0 && (
                        <span className="discount-chip">-{item.discount_percentage}%</span>
                      )}
                      <span className="line-total">{formatCurrency(item.subtotal ?? item.total)}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="product-item-delete"
                    onClick={() => removeItem(index)}
                    title="Eliminar producto"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            {esCotizacion && (
              <div className="cotizacion-totals">
                <div className="cotizacion-totals-row">
                  <span>Subtotal</span>
                  <span>{formatCurrency(totals.subtotal)}</span>
                </div>
                {totals.descuento > 0 && (
                  <div className="cotizacion-totals-row cotizacion-totals-row--discount">
                    <span>Descuento</span>
                    <span>-{formatCurrency(totals.descuento)}</span>
                  </div>
                )}
                <div className="cotizacion-totals-row cotizacion-totals-row--final">
                  <span>Total</span>
                  <span>{formatCurrency(totals.total)}</span>
                </div>
              </div>
            )}

            {validationErrors.items && (
              <div className="products-error">{validationErrors.items}</div>
            )}
          </div>
        ) : (
          <div className="preorder-empty-state">
            <div className="empty-state-icon">
              <FiPackage size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la pre-orden
            </p>
            {validationErrors.items && (
              <div className="products-error">{validationErrors.items}</div>
            )}
          </div>
        )}
      </form>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default PreOrderForm;
